import axios, { AxiosError, AxiosInstance } from "axios";
import { useMutation } from "react-query";
import {
  DateRangeReportRequest,
  PostingSheetReportRequest,
} from "../request/ReportGenerationRequest";
import attachAuthInterceptors from "./attachAuthInterceptors";

const createReportsClient = (): AxiosInstance => {
  // Empty baseURL = same-origin. Firebase Hosting rewrites /api/reports/**
  // to the insights Cloud Run service. Local dev sets the env var to the
  // local FastAPI URL (e.g. http://localhost:8001) to bypass the rewrite.
  const baseURL = process.env.REACT_APP_TA_INSIGHTS_SERVICE_URL || "";

  const client = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
    responseType: "blob",
  });
  attachAuthInterceptors(client);
  return client;
};

const reportsClient = createReportsClient();

const PlannedVsActualsReportApiService = async (
  request: DateRangeReportRequest
): Promise<Blob> => {
  const { data } = await reportsClient.post<Blob>(
    "/api/reports/planned-vs-actuals",
    request
  );
  return data;
};

const TimesheetReportApiService = async (
  request: DateRangeReportRequest
): Promise<Blob> => {
  const { data } = await reportsClient.post<Blob>(
    "/api/reports/timesheet",
    request
  );
  return data;
};

const PostingSheetReportApiService = async (
  request: PostingSheetReportRequest
): Promise<Blob> => {
  const { data } = await reportsClient.post<Blob>(
    "/api/reports/posting-sheet",
    request
  );
  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const usePlannedVsActualsReportApiService = () =>
  useMutation<Blob, AxiosError, DateRangeReportRequest>(
    PlannedVsActualsReportApiService,
    { mutationKey: "PlannedVsActualsReportApiService" }
  );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useTimesheetReportApiService = () =>
  useMutation<Blob, AxiosError, DateRangeReportRequest>(
    TimesheetReportApiService,
    { mutationKey: "TimesheetReportApiService" }
  );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const usePostingSheetReportApiService = () =>
  useMutation<Blob, AxiosError, PostingSheetReportRequest>(
    PostingSheetReportApiService,
    { mutationKey: "PostingSheetReportApiService" }
  );

export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  link.remove();
};

export const formatIsoDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
